import { useEffect, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import Select from "react-select";
import {
    Button,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

import {
    getAssignedFunctionKeysForUser,
    getSelectedKeys,
    shouldConfirmDpoReplace,
} from "./helpers";
import useUserFunctionsSelect from "./hooks/useUserFunctionsSelect";

const SELECT_STYLES = {
    container: (base) => ({ ...base, minWidth: 180 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const UserFunctionsSelect = ({
    t,
    value,
    onChange,
    userId,
    hideLabel = false,
    onInitialized,
    inputId = "user-functions-select",
}) => {
    const { functions, options, isLoading, isFetched } =
        useUserFunctionsSelect(t);
    const initializedForRef = useRef(null);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    useEffect(() => {
        if (!userId || !isFetched) {
            return;
        }

        if (initializedForRef.current === userId) {
            return;
        }

        const keys = getAssignedFunctionKeysForUser(functions, userId);
        onChange(options.filter((option) => keys.includes(option.value)));
        initializedForRef.current = userId;
        onInitialized?.();
        // Parent onChange/onInitialized are stable enough for one-time init.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, functions, options, isFetched]);

    const closeConfirm = () => {
        setIsConfirmOpen(false);
        setPendingSelection(null);
    };

    const handleChange = (nextSelected) => {
        const next = nextSelected || [];

        if (
            shouldConfirmDpoReplace({
                functions,
                selectedKeys: getSelectedKeys(value),
                nextKeys: getSelectedKeys(next),
                currentUserId: userId,
            })
        ) {
            setPendingSelection(next);
            setIsConfirmOpen(true);
            return;
        }

        onChange(next);
    };

    const handleConfirmReplace = () => {
        onChange(pendingSelection || []);
        closeConfirm();
    };

    return (
        <>
            {!hideLabel && (
                <Label className="form-label" htmlFor={inputId}>
                    {`${t("Function")}:`}
                </Label>
            )}
            <Select
                inputId={inputId}
                classNamePrefix="select2-selection"
                isMulti
                isClearable
                isDisabled={isLoading}
                isLoading={isLoading}
                menuPortalTarget={document.body}
                onChange={handleChange}
                options={options}
                placeholder={t("Select...")}
                styles={SELECT_STYLES}
                value={value || []}
            />

            <Modal isOpen={isConfirmOpen} backdrop="static">
                <ModalHeader toggle={closeConfirm}>
                    {t("Function")}
                </ModalHeader>
                <ModalBody>
                    {t("This will replace the current DPO assignment.")}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        type="button"
                        onClick={closeConfirm}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        color="primary"
                        type="button"
                        onClick={handleConfirmReplace}
                    >
                        {t("Confirm")}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default withNamespaces()(UserFunctionsSelect);
